import React, { ChangeEvent, MouseEvent, useRef } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import {
  Modal,
  StackLayout,
  Box,
  Button,
  styled,
} from '@leagueplatform/genesis-core';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SUB_PRODUCT_AREA,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import { useContentBlobUrl } from '@leagueplatform/league-content-api';
import { useUploadProfilePhoto } from '../../../hooks/use-upload-profile-photo.hook';
import { usePersonalInformation } from '../../../hooks/use-personal-information.hook';
import { useUpdateUserProfile } from '../../../hooks/use-update-user-profile.hook';
// import { ProfileImageErrorModal } from 'components/modals/profile-image-error-modal.component';

const InvisibleInput = styled('input', {
  display: 'none',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

export const EditProfilePhoto = () => {
  const { formatMessage } = useIntl();

  const { data } = usePersonalInformation();

  const [
    { profilePhotoId, uploadError },
    { setUploadError, uploadProfilePhoto, resetProfilePhotoId },
  ] = useUploadProfilePhoto(data);

  const { updateUserProfile } = useUpdateUserProfile({
    onSuccess: () => {
      toastActions.add({
        type: TOAST_STATUS.SUCCESS,
        textId: 'STR_PROFILE_PHOTO_UPDATED',
      });
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: PRODUCT_AREA.SETTINGS,
        sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
        screen_name: SUB_PRODUCT_AREA.PERSONAL_INFO,
        details: 'Profile photo update succeeded',
      });
    },
    onError: () => {
      resetProfilePhotoId();
      toastActions.add({
        type: TOAST_STATUS.ERROR,
        textId: 'PROFILE_PHOTO_UPDATE_FAILED',
      });
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: PRODUCT_AREA.SETTINGS,
        sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
        screen_name: SUB_PRODUCT_AREA.PERSONAL_INFO,
        details: 'Profile photo update failed',
      });
    },
  });

  const initials = data?.userInitials;
  const inputRef = useRef<HTMLInputElement>(null);
  const profilePhotoUrl = useContentBlobUrl(profilePhotoId || null);

  return (
    <>
      <Modal.Root open={uploadError} onOpenChange={() => setUploadError(false)}>
        {/* <ProfileImageErrorModal /> */}
      </Modal.Root>
      <StackLayout horizontalAlignment="center">
        <Box css={{ position: 'relative' }}>
          <Box
            data-testid="profile-image"
            className={profilePhotoId ? 'background-image' : ''}
            css={{
              width: 128,
              height: 128,
              borderRadius: '$circle',
              backgroundColor: '$surfaceCardPrimary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              typography: '$headingOne',
              ...(profilePhotoUrl && {
                backgroundImage: `url(${profilePhotoUrl})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '50%',
              }),
            }}
          >
            {!profilePhotoId && initials}
          </Box>
          <InvisibleInput
            id="photo-file-input"
            ref={inputRef}
            type="file"
            accept="image/*"
            data-testid="file-input"
            onChange={async (e: ChangeEvent<HTMLInputElement>) => {
              if (!e.currentTarget.files) return;
              const file = e?.currentTarget?.files[0];
              if (file) {
                trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                  product_area: PRODUCT_AREA.SETTINGS,
                  sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
                  screen_name: SUB_PRODUCT_AREA.PERSONAL_INFO,
                  details: 'User selected a profile photo to update',
                });
                const newID = await uploadProfilePhoto(file);
                if (newID) {
                  trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                    product_area: PRODUCT_AREA.SETTINGS,
                    sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
                    screen_name: SUB_PRODUCT_AREA.PERSONAL_INFO,
                    details: 'User initiated profile photo update',
                  });
                  updateUserProfile({ avatar: { imageId: newID } });
                }
              }
            }}
            onClick={(e: MouseEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).value = '';
            }}
          />
          <Button
            hideLabel
            icon="interfaceEdit"
            size="small"
            onClick={() => {
              trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                product_area: PRODUCT_AREA.SETTINGS,
                sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
                screen_name: SUB_PRODUCT_AREA.PERSONAL_INFO,
                details: 'User clicks add/change your profile photo button',
              });
              inputRef?.current?.click();
            }}
            css={{
              position: 'absolute',
              bottom: 0,
              right: '$half',
              borderRadius: '50%',
              width: '$two',
              height: '$two',
            }}
          >
            {formatMessage({
              id: profilePhotoId
                ? 'STR_UPDATE_PROFILE_PHOTO'
                : 'STR_ADD_PROFILE_PHOTO',
            })}
          </Button>
        </Box>
      </StackLayout>
    </>
  );
};
